import { partnerWorkflowStatus } from "./medicalPartnerListTableHelpers";

export async function approveMedicalPartnerAsLive(http, partnerId, { quietUserWarning = false, swal } = {}) {
  const partnerResponse = await http.put(`/api/v1/medical-partners/${partnerId}`, { status: "active" });
  const partner = partnerResponse.data?.data || partnerResponse.data;
  const userId = partner?.userId?._id || partner?.userId;

  if (partner && userId) {
    try {
      const userResponse = await http.get(`/api/v1/user/${userId}`);
      const currentUser = userResponse.data?.data || userResponse.data;
      const updateData = { medicalPartnerId: partnerId };
      if (currentUser?.role !== "admin" && currentUser?.role !== "medical_partner") {
        updateData.role = "medical_partner";
      }
      await http.put(`/api/v1/user/${userId}`, updateData);
    } catch (userError) {
      if (process.env.NODE_ENV === "development") {
        console.error(userError);
      }
      if (!quietUserWarning && swal) {
        swal("Warning", "Partner approved but error updating user", "warning");
      }
    }
  }
}

export async function putPartnerWorkflowStatus(http, partnerId, status) {
  await http.put(`/api/v1/medical-partners/${partnerId}`, { status });
}

/**
 * Same rules as the medical partner list status control. No success toast here — caller shows it.
 * @returns {Promise<boolean>} true if the API was updated
 */
export async function runPartnerWorkflowTransition(partner, nextStatus, { http, swal }) {
  if (!partner?._id) return false;
  const id = partner._id;
  const name = partner.organization_name || "this partner";
  const current = partnerWorkflowStatus(partner);
  if (current === nextStatus) return false;

  const confirm = (opts) => swal(opts).then((ok) => Boolean(ok));

  if (nextStatus === "active") {
    if (current === "pending") {
      const ok = await confirm({
        title: "Set partner to Live?",
        text: `Verify "${name}" and set workflow to Live (API active). Links the primary user as medical_partner where applicable.`,
        icon: "info",
        buttons: ["Cancel", "Set Live"],
      });
      if (!ok) return false;
      await approveMedicalPartnerAsLive(http, id, { swal });
      return true;
    }
    if (current === "inactive" || current === "draft") {
      const ok = await confirm({
        title: "Set partner to Live?",
        text: `Set "${name}" to Live (API active). Public listing should still respect Live on API and site.`,
        icon: "info",
        buttons: ["Cancel", "Set Live"],
      });
      if (!ok) return false;
      await putPartnerWorkflowStatus(http, id, "active");
      return true;
    }
    return false;
  }

  if (nextStatus === "inactive") {
    const ok = await confirm({
      title: "Set partner inactive?",
      text: `Partner "${name}" will be marked inactive.`,
      icon: "warning",
      buttons: ["Cancel", "Set inactive"],
      dangerMode: true,
    });
    if (!ok) return false;
    await putPartnerWorkflowStatus(http, id, "inactive");
    return true;
  }

  if (nextStatus === "pending" && current === "active") {
    const ok = await confirm({
      title: "Set status to pending?",
      text: `"${name}" will show as pending until reviewed again.`,
      icon: "warning",
      buttons: ["Cancel", "Set pending"],
    });
    if (!ok) return false;
    await putPartnerWorkflowStatus(http, id, "pending");
    return true;
  }

  if (nextStatus === "draft") {
    const ok = await confirm({
      title: "Set status to draft?",
      text: `"${name}" will be marked as draft.`,
      icon: "info",
      buttons: ["Cancel", "Set draft"],
    });
    if (!ok) return false;
    await putPartnerWorkflowStatus(http, id, "draft");
    return true;
  }

  const ok = await confirm({
    title: "Update partner status?",
    text: `Change "${name}" to ${nextStatus}.`,
    icon: "info",
    buttons: ["Cancel", "Confirm"],
  });
  if (!ok) return false;
  await putPartnerWorkflowStatus(http, id, nextStatus);
  return true;
}
